import { queryOptions, useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { QueryConfig } from '@/lib/api-clients/react-query';
import { privateApi } from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';
import { OrderStatus, TransferType } from '@/types/enums';
interface GetTransfersParams {
  id?: string;
  orderType?: string;
  startTime?: number;
  endTime?: number;
  limit?: number;
}

type NewTransferResponse = {
  id: string;
  size: string;
  orderType: TransferType;
  status: OrderStatus.NEW;
  chainId: number;
  postTime: number;
  isSolanaNative: boolean;
};

type ProcessingTransferResponse = {
  id: string;
  size: string;
  orderType: TransferType;
  status: OrderStatus.PROCESSING;
  chainId: number;
  postTime: number;
  isSolanaNative: boolean;
};

type FilledTransferResponse = {
  id: string;
  size: string;
  orderType: TransferType;
  status: OrderStatus.FILLED;
  chainId: number;
  postTime: number;
  isSolanaNative: boolean;
};

type RejectedTransferResponse = {
  id: string;
  orderType: TransferType;
  status: OrderStatus.REJECTED;
  code: number;
  chainId: number;
  postTime: number;
  isSolanaNative: boolean;
};

type VerifiedTransferResponse = {
  id: string;
  orderType: TransferType;
  status: OrderStatus.VERIFIED;
  size: string;
  chainId: number;
  postTime: number;
  requestArgs: string;
  signer: string;
  signature: string;
  signatureProof: string;
  validatorSignature: string;
  isSolanaNative: boolean;
};

export type TransferResponse =
  | NewTransferResponse
  | FilledTransferResponse
  | RejectedTransferResponse
  | VerifiedTransferResponse
  | ProcessingTransferResponse;

export const getTransfers = async (
  { id, orderType, startTime, endTime, limit }: GetTransfersParams,
  apiKey?: string,
): Promise<TransferResponse[]> => {
  return privateApi.get('/transfer', {
    params: {
      id,
      orderType,
      startTime,
      endTime,
      limit,
    },
    ...(apiKey
      ? {
          headers: {
            'X-API-KEY': apiKey,
          },
        }
      : {}),
  });
};

export const getTransfersQueryOptions = (
  params: GetTransfersParams,
  apiKeyOverride?: string,
  address?: string,
) => {
  const { getAccurateTime, getAuthParams } = useAccountStore.getState();

  const { endTime = getAccurateTime(), limit = 1000, ...restOfParams } = params;
  const apiKey = apiKeyOverride ?? getAuthParams(address)?.apiKey;

  const paramsToCache = { ...restOfParams };
  return queryOptions({
    queryKey: ['transfers', paramsToCache, address, apiKey],
    queryFn: () => getTransfers({ endTime, limit, ...restOfParams }, apiKey),
  });
};

type UseTransfersOptions = GetTransfersParams & {
  queryConfig?: QueryConfig<typeof getTransfersQueryOptions>;
};

export const useTransfers = ({
  queryConfig,
  ...params
}: UseTransfersOptions = {}) => {
  const { hasAuth, accGroup, apiKey } = useAccountStore(
    useShallow((state) => {
      const authParams = state.getAuthParams();
      return {
        hasAuth: state.hasAuth,
        apiKey: authParams?.apiKey,
        accGroup: authParams?.accountGroup,
      };
    }),
  );

  return useQuery({
    enabled: hasAuth && !!apiKey && accGroup !== undefined && accGroup !== 2,
    ...getTransfersQueryOptions(params),
    ...queryConfig,
  });
};
